import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import CreditCardIcon from '@mui/icons-material/CreditCard'

import { logo } from '../../images/images.js'
import { getBillById, getFOIStatus } from '../../dataClient/apiCalls'

import { formatFOIForPaymentFromBill } from '../../util/foiPaymentStateFormatting'
import { getBillStepperArray } from '../../util/billingConsts'
import { responsePageReadOnlyStatuses } from '../../util/foiConsts'
import { PageWithTitle, Header, FlexColumn, SpaceBetweenRowContainer, Logo, LightBGContainer, FlexRow } from '../styles/Containers'
import { PageTitle, MedBodyText } from '../styles/Text'
import Carousel from '../common/Carousel'
import Loading from '../common/Loading'
import LoadingError from '../common/LoadingError'
import Bill from '../bill/Bill'
import RejectEstimateButton from '../billActions/RejectEstimateButton'
import CancelRequestButton from '../billActions/CancelRequestButton'
import BillRejectEstimateConfirmation from '../billConfirmations/BillRejectEstimateConfirmation'
import BillCancelledConfirmation from '../billConfirmations/BillCancelledConfirmation'
import Button from '../common/Button'
import { InfoMessage, WarningMessage } from '../common/UserMessage'
import { formatBillFromServer } from '../../util/billFormatting.js'

function BillReviewPage() {
  const navigate = useNavigate()
  const { billId } = useParams()

  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState()
  const [bill, setBill] = useState()
  const [foiStatus, setFoiStatus] = useState()
  const [showRejectConfirmation, setShowRejectConfirmation] = useState(false)
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)

  function getBillDetails(billId) {
    getBillById(billId)
      .then(({ data }) => {
        const formattedBill = formatBillFromServer(data.data)
        setBill(formattedBill)

        if (formattedBill.foiId) {
          loadFoiStatus(formattedBill.foiId)
        } else {
          setLoading(false)
        }
      })
      .catch(e => {
        console.error('Failed to retrieve bill details', billId, e)
        if (e.response?.data?.message?.includes('billId not found')) {
          setErrorMessage(['The provided bill does not exist.'])
        } else {
          setErrorMessage([])
        }
        setLoading(false)
      })
  }

  function loadFoiStatus(foiId) {
    getFOIStatus(foiId)
      .then(({ data }) => {
        setFoiStatus(data.data)
      })
      .catch(e => {
        console.error('Failed to retrieve FOI status', foiId, e)
      })
      .finally(() => setLoading(false))
  }

  function handleRejectSuccess() {
    setShowRejectConfirmation(true)
  }

  function handleCancelSuccess() {
    setShowCancelConfirmation(true)
  }

  function handleProceedToPayment() {
    const foiPaymentSnapshot = formatFOIForPaymentFromBill({ bill, foiStatus })
    if (foiPaymentSnapshot) {
      navigate(`/payment/bill/${billId}`, { state: { foiPaymentSnapshot } })
    } else {
      navigate(`/payment/bill/${billId}`)
    }
  }

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    setLoading(true)
    setErrorMessage(undefined)
    setBill()
    setFoiStatus()
    setShowRejectConfirmation(false)
    setShowCancelConfirmation(false)
    if (billId) getBillDetails(billId)
  }, [billId])

  if (loading) return <Loading />

  if (errorMessage !== undefined) {
    return <LoadingError message={errorMessage} />
  }

  if (!bill) {
    return <LoadingError message={['Unable to load bill details.']} />
  }

  const isFeeEstimate = bill.type === 'FEE_ESTIMATE'
  const foiSecondaryId = foiStatus?.secondaryId
  const foiStatusValue = foiStatus?.status
  const readOnly =
    bill.status !== 'WAITING' || (foiStatusValue !== null && foiStatusValue !== undefined && responsePageReadOnlyStatuses.includes(foiStatusValue))

  let readOnlyMessage
  if (readOnly) {
    if (foiStatusValue && responsePageReadOnlyStatuses.includes(foiStatusValue)) {
      readOnlyMessage = 'The Freedom of Information request has been completed or closed. No further actions are available on this page.'
    } else {
      switch (bill.status) {
        case 'PAID':
          readOnlyMessage = 'This bill has already been paid.'
          break
        case 'REJECTED':
          readOnlyMessage = 'This estimate was rejected as requested.'
          break
        case 'CANCELLED':
          readOnlyMessage = 'This bill has been cancelled.'
          break
        case 'REFUNDED':
          readOnlyMessage = 'This bill has been refunded.'
          break
        default:
          readOnlyMessage = 'This bill is no longer available for payment.'
      }
    }
  }

  if (showRejectConfirmation && foiSecondaryId) {
    return <BillRejectEstimateConfirmation foiSecondaryId={foiSecondaryId} billSecondaryId={bill.secondaryId} />
  }

  if (showCancelConfirmation && foiSecondaryId) {
    return <BillCancelledConfirmation secondaryId={foiSecondaryId} />
  }

  return (
    <PageWithTitle gap="40px">
      <Header>
        <Logo src={logo} />
        <PageTitle>{foiSecondaryId ? `${foiSecondaryId} ` : ''}Bill Payment</PageTitle>
      </Header>
      <FlexColumn gap="30px">
        {!readOnly && (
          <SpaceBetweenRowContainer gap="50px">
            <div />
            <Carousel steps={getBillStepperArray()} activeStep={0} />
          </SpaceBetweenRowContainer>
        )}
        <FlexColumn gap="20px">
          {readOnly && readOnlyMessage && (
            <WarningMessage>
              <MedBodyText>{readOnlyMessage}</MedBodyText>
            </WarningMessage>
          )}
          <LightBGContainer padding="50px">
            <FlexColumn gap="15px">
              <Bill bill={bill} />
            </FlexColumn>
          </LightBGContainer>
          {!readOnly && (
            <FlexColumn gap="15px">
              {isFeeEstimate && (
                <InfoMessage>
                  If you do not wish to continue with your freedom of information request, you can close the request. You will not be refunded for any
                  work already done.
                </InfoMessage>
              )}
              <SpaceBetweenRowContainer>
                {isFeeEstimate && (
                  <FlexRow gap="15px">
                    {bill.foiId && foiSecondaryId && (
                      <CancelRequestButton foiId={bill.foiId} secondaryId={foiSecondaryId} onSuccess={handleCancelSuccess} />
                    )}
                    <RejectEstimateButton billId={bill.id} secondaryId={bill.secondaryId} onSuccess={handleRejectSuccess} />
                  </FlexRow>
                )}
                {!isFeeEstimate && <div />}
                <Button onClick={handleProceedToPayment}>
                  <CreditCardIcon />
                  Confirm & Proceed to Payment
                </Button>
              </SpaceBetweenRowContainer>
            </FlexColumn>
          )}
        </FlexColumn>
      </FlexColumn>
    </PageWithTitle>
  )
}

export default BillReviewPage
